import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "@/i18n";

const ProjectModal = ({ project, img, onClose }) => {
  const { t } = useLang();

  useEffect(() => {
    if (project) {
      document.body.style.overflow = "hidden";
      const onKey = (e) => e.key === "Escape" && onClose();
      window.addEventListener("keydown", onKey);
      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("keydown", onKey);
      };
    }
  }, [project, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="project-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/50 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="grid max-h-[92vh] w-full max-w-3xl grid-cols-1 overflow-y-auto bg-bone shadow-[0_30px_80px_rgba(0,0,0,0.3)] md:grid-cols-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64 bg-sand md:h-auto">
              <img src={img} alt={project.name} className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="flex flex-col p-8 md:p-10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-clay">{project.client}</p>
                  <h3 className="mt-2 font-serif text-3xl tracking-tight text-ink">{project.name}</h3>
                </div>
                <button
                  onClick={onClose}
                  data-testid="project-modal-close"
                  aria-label={t.modal.close}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-ink/20 text-ink transition-colors duration-300 hover:border-clay hover:text-clay"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-8">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">{t.modal.briefL}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/70">{project.brief}</p>
              </div>

              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">{t.modal.handledL}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {project.handled.map((h) => (
                    <span key={h} className="rounded-full border border-clay/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-clay">
                      {h}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 border-t border-ink/10 pt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/40">{t.modal.resultL}</p>
                <p className="mt-2 font-serif text-lg italic text-ink">{project.result}</p>
              </div>

              <a
                href="#quote"
                onClick={onClose}
                data-testid="project-modal-quote"
                className="mt-8 inline-flex w-fit rounded-full bg-ink px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay"
              >
                {t.nav.quote}
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
